import { useCallback, useMemo } from 'react'
import { useGetVehicleReturnsQuery } from '../redux/api/ordersApi'
import { ContractStateEnum } from '../types/enums'
import { Arr } from '../helpers/Arr'
import { ContractViewModel } from '@ail/contracts-api'
import { ApprovalProcessStatus } from '@ail/orders-api'
import { useTranslation } from 'react-i18next'
import { GridRow } from '../components/GeneralContracts/GeneralContractsFunctional'
import { SelectionState } from '../components/GeneralContracts/ReplacementState/ReplacementStateSelector'

interface Props {
  endIntention?: string
  endDate?: Date | null
  state?: string
  gallikerReference?: string
  futureContract?: ContractViewModel
  expiringContractsFilter: string
}

export function useReplacementCondition() {
  const { t } = useTranslation('common')

  const vehicleReturns = useGetVehicleReturnsQuery()

  const vehicleReturnsMap = useMemo(() => {
    return Arr.toHashMap(vehicleReturns.currentData ?? [], (r) => r.logisticId)
  }, [vehicleReturns.currentData])

  const getReplacementCondition = useCallback(
    ({ endIntention, endDate, state, gallikerReference, futureContract, expiringContractsFilter }: Props) => {
      const replacementWindow = Math.max(+expiringContractsFilter, 180) * 1000 * 60 * 60 * 24

      let shouldHaveReplacement = endIntention === 'Undefined' || endIntention === 'VehicleToLogistic'
      shouldHaveReplacement &&= endDate != null && endDate.getTime() - new Date().getTime() < replacementWindow

      const invalidStatus = [
        ContractStateEnum.Closed,
        ContractStateEnum.Initialized,
        ContractStateEnum.Terminated,
        ContractStateEnum.Canceled,
      ].includes((state ?? '') as ContractStateEnum)

      const replaceId = shouldHaveReplacement && gallikerReference ? `${gallikerReference}R` : undefined
      const replaceOrder = replaceId ? vehicleReturnsMap[replaceId] : undefined

      const baseCondition = !futureContract && shouldHaveReplacement && !invalidStatus
      const hasReplaceOrder = !!replaceOrder
      const hasNewLogisticId = !!replaceOrder?.newLogisticId

      const orderIsApproved = replaceOrder?.approvalStatus === ApprovalProcessStatus.Approved
      const orderIsPending = replaceOrder?.approvalStatus === ApprovalProcessStatus.Pending

      const orderPlacedButNotApproved = baseCondition && hasReplaceOrder && orderIsPending
      const orderApprovedButMissingContract = baseCondition && hasReplaceOrder && hasNewLogisticId && orderIsApproved

      const missingReplacementOrder = baseCondition && !hasReplaceOrder

      const processingOrder = orderPlacedButNotApproved || orderApprovedButMissingContract

      const returnWithoutReplacement = baseCondition && hasReplaceOrder && !hasNewLogisticId && !processingOrder

      return { missingReplacementOrder, returnWithoutReplacement, processingOrder }
    },
    [vehicleReturnsMap]
  )

  const getReplacementStateExportString = useCallback(
    (row: GridRow) => {
      const missingOrder = row.replacementOrderCondition?.missingReplacementOrder
      const noReplacement = row.replacementOrderCondition?.returnWithoutReplacement
      const processing = row.replacementOrderCondition?.processingOrder
      const replacementCellText =
        missingOrder && row.futureContract
          ? t('MISSING_REPLACEMENT_ORDER')
          : noReplacement && !row.futureContract
          ? t('RETURN_WITHOUT_REPLACEMENT')
          : processing && !row.futureContract
          ? `${t('STATUS_PENDING')} / ${t('PROCESSING')}`
          : !!row.futureContract && !missingOrder && !noReplacement
          ? row.futureContract.reference!
          : t('NO_DATA')

      return replacementCellText
    },
    [t]
  )

  const calculateReplacementSortValue = useCallback((row: GridRow) => {
    const { futureContract, replacementOrderCondition: condition } = row
    if (!futureContract && !condition?.missingReplacementOrder && !condition?.returnWithoutReplacement) return 3
    if (condition?.missingReplacementOrder) return 2
    if (futureContract && !condition?.missingReplacementOrder) return 1
    if (!futureContract && condition?.returnWithoutReplacement) return 0
    return 3
  }, [])

  const selectedReplacementFormat = useCallback(
    (value?: SelectionState) => {
      if (!value) return ''
      const { hasReplacement, missingReplacement, noData, returnWithoutReplacement, processingOrder } = value
      const activeSelection = Object.values(value).filter((val) => !!val).length
      let string = ''

      if (activeSelection > 1) {
        string = t('MULTIPLE_SELECTED')
        return string
      }

      if (hasReplacement) {
        string = t('HAS_REPLACEMENT_ORDER')
      } else if (missingReplacement) {
        string = t('MISSING_REPLACEMENT_ORDER')
      } else if (noData) {
        string = t('NO_DATA')
      } else if (processingOrder) {
        string = `${t('STATUS_PENDING')} / ${t('PROCESSING')}`
      } else if (returnWithoutReplacement) string = t('RETURN_WITHOUT_REPLACEMENT')

      return string
    },
    [t]
  )

  return {
    getReplacementCondition,
    getReplacementStateExportString,
    calculateReplacementSortValue,
    selectedReplacementFormat,
  } as const
}
